import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { MapPin, DollarSign, Briefcase, Bookmark } from 'lucide-react';
import AdvancedSearch from '../components/AdvancedSearch';
import { useSavedJobs } from '../context/SavedJobsContext';

export default function Jobs() {
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    location: ''
  });

  const { saveJob, removeJob, isSaved } = useSavedJobs();

  // Set initial category from URL params
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setFilters(prev => ({ ...prev, category: categoryFromUrl }));
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.category) params.append('category', filters.category);
        if (filters.location) params.append('location', filters.location);

        const response = await api.get(`/api/jobs?${params.toString()}`);
        // Some backends may return a wrapper object like { value: [...], Count: n }
        const payload = response.data && response.data.value ? response.data.value : response.data;
        setJobs(Array.isArray(payload) ? payload : []);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
        // Fallback sample data so page remains attractive when backend is down
        setJobs([
          {
            id: 'sample-1',
            title: 'Frontend Engineer (React)',
            employer_name: 'Acme Tech',
            location: 'Harare, Zimbabwe',
            salary_min: 800,
            salary_max: 1500,
            experience_level: 'Mid Level',
            description: 'Build beautiful, performant user interfaces and ship delightful user experiences.',
            tags: ['React', 'Tailwind', 'Remote'],
            posted_at: new Date().toISOString(),
            featured: true
          },
          {
            id: 'sample-2',
            title: 'Junior Data Analyst',
            employer_name: 'DataWorks',
            location: 'Bulawayo, Zimbabwe',
            salary_min: 400,
            salary_max: 700,
            experience_level: 'Entry Level',
            description: 'Support data-driven decisions by cleaning and analyzing datasets.',
            tags: ['SQL', 'Excel'],
            posted_at: new Date().toISOString(),
            featured: false
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [filters]);

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl p-6 sm:p-8 md:p-12 mb-8 shadow-xl">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4">Browse Available Vacancies</h1>
          <p className="text-base sm:text-lg md:text-xl text-blue-100 mb-4 md:mb-6">
            Talent looking for a new career opportunity - Browse available roles
          </p>
        </div>

        {/* Sector Quick Links */}
        <div className="mb-8 bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <h3 className="font-semibold text-gray-700 mb-4 text-sm sm:text-base">Filter by Sector:</h3>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {['All', 'Mining & Quarrying', 'Agriculture', 'Banking & Finance Services', 'Human Resources, Education, Legal, IT & Consultancy', 'Marketing', 'Forestry & Fishing', 'Engineering & Industrial', 'Construction', 'Energy', 'Wholesale & Retail Trade', 'Transport & Logistics', 'Procurement & Supply Chain', 'Administration & Support Services', 'Parastatals & NECs', 'Other Sectors'].map((sector) => (
              <button
                key={sector}
                onClick={() => setFilters(prev => ({ ...prev, category: sector === 'All' ? '' : sector }))}
                className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition text-sm whitespace-nowrap ${
                  (sector === 'All' && !filters.category) || filters.category === sector
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {sector}
              </button>
            ))}
          </div>
        </div>

        {/* Featured jobs */}
        {Array.isArray(jobs) && jobs.some(j => j.featured) && (
          <div className="mb-6">
            <h2 className="text-2xl font-semibold mb-4">Featured Vacancies</h2>
            <div className="flex gap-4 overflow-x-auto pb-4">
              {jobs.filter(j => j.featured).map(j => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="min-w-[320px] bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition border-l-4 border-yellow-400">
                  <h3 className="font-bold">{j.title}</h3>
                  <p className="text-sm text-gray-600">{j.employer_name} • {j.location}</p>
                  <div className="mt-2 text-xs text-gray-500">{j.tags?.slice(0,3).map(t => <span key={t} className="mr-2">#{t}</span>)}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <AdvancedSearch filters={filters} setFilters={setFilters} />

        {/* Jobs List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading vacancies...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <Briefcase className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500 text-lg">No vacancies found. Try adjusting your filters.</p>
            </div>
          ) : (
            jobs.map((job) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition border ${job.featured ? 'border-yellow-400' : 'border-transparent'}`}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 font-bold text-sm">
                    {job.employer_name ? job.employer_name.split(' ').map(n=>n[0]).slice(0,2).join('') : 'Co'}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-gray-800 mb-1">{job.title}</h3>
                      {job.featured && <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">Featured</span>}
                    </div>

                    <p className="text-gray-600 mb-3 line-clamp-2">{job.description || 'No description provided.'}</p>

                    <div className="flex flex-wrap gap-2 items-center text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Briefcase size={14} />
                        <span>{job.employer_name}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPin size={14} />
                        <span>{job.location}</span>
                      </div>
                      {job.salary_min && (
                        <div className="flex items-center space-x-1">
                          <DollarSign size={14} />
                          <span>${job.salary_min} - ${job.salary_max}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex items-center gap-2">
                      {job.tags?.slice(0,3).map((t) => (
                        <span key={t} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">{t}</span>
                      ))}
                    </div>
                  </div>

                  <div className="text-right">
                    <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">{job.experience_level}</span>
                    <div className="mt-4">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={(e) => { e.preventDefault(); isSaved(job.id) ? removeJob(job.id) : saveJob(job); }}
                            className={`px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${isSaved(job.id) ? 'bg-primary-deep text-white' : 'bg-secondary-verylight text-gray-800'}`}
                          >
                            <Bookmark size={16} /> {isSaved(job.id) ? 'Saved' : 'Save'}
                          </button>
                          <button className="bg-primary-medium text-white px-3 py-2 rounded-lg text-sm font-semibold">View</button>
                        </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
